// Page-level console layout: the page header, the panel, and the panel grid.
// Pages compose these instead of writing grids, so every screen shares one
// left content line, one gap ladder, and one panel shape.
import { useId, type CSSProperties, type ReactNode } from "react";
import { motion } from "motion/react";
import { useEnterMotion } from "./motion";
import { BENTO_WORDS, type BentoPlacement } from "./bento";
import { flowBento, type FlowCard } from "./flow";

export interface PageHeaderProps {
  /** The page's one h1, e.g. "Dashboard". */
  title: string;
  /** One line that says what this page answers. */
  description?: ReactNode;
  /** Page-level controls: a SegmentedControl, a secondary button. One primary at most. */
  actions?: ReactNode;
}

/** Title, one line of description, optional actions. Never boxed. */
export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className="con-page-header">
      <div className="con-page-header-text">
        <h1 className="con-page-title">{title}</h1>
        {description ? <p className="con-page-description">{description}</p> : null}
      </div>
      {actions ? <div className="con-page-actions">{actions}</div> : null}
    </div>
  );
}

export type PanelGridLayout = "halves" | "thirds" | "main-side" | "side-main";

export interface PanelGridProps {
  /**
   * halves     1 column, 2 equal from 768
   * thirds     1 column, 3 equal from 1280
   * main-side  1 column, 2:1 from 1280
   * side-main  1 column, 1:2 from 1280
   * Panels in one row stretch to one height; put a chart beside a chart and
   * a list beside a list so no panel holds an empty band.
   */
  layout?: PanelGridLayout;
  children: ReactNode;
}

export function PanelGrid({ layout = "halves", children }: PanelGridProps) {
  return (
    <div className="con-grid" data-layout={layout}>
      {children}
    </div>
  );
}

export interface PanelProps {
  /** Panel heading (h2). Every panel names what it holds. */
  title: string;
  /** One supporting line under the heading. */
  description?: ReactNode;
  /** One control at the heading's end (a link, a ghost button, a badge). */
  action?: ReactNode;
  /** Controls that act on the body (FilterChips, a search field), between the heading and the body. */
  toolbar?: ReactNode;
  /** Pinned to the panel bottom with margin-top auto: a source line, a link. */
  footer?: ReactNode;
  /** "default" surface plus hairline, or "flush" when the body is a table or list that runs edge to edge. */
  variant?: "default" | "flush";
  /** Heading level when the panel sits under another heading. */
  headingLevel?: 2 | 3;
  id?: string;
  children: ReactNode;
}

/**
 * One self-contained widget: one chart, one list, one table, or one form.
 * Never a panel inside a panel. The body stretches only when it is a chart.
 */
export function Panel({ title, description, action, toolbar, footer, variant = "default", headingLevel = 2, id, children }: PanelProps) {
  const headingId = `${useId()}title`;
  const enter = useEnterMotion();
  const Heading = headingLevel === 3 ? "h3" : "h2";
  return (
    <motion.section className="con-panel" data-variant={variant} aria-labelledby={headingId} id={id} {...enter}>
      <div className="con-panel-head">
        <div className="con-panel-head-text">
          <Heading id={headingId} className="con-panel-title">
            {title}
          </Heading>
          {description ? <p className="con-panel-description">{description}</p> : null}
        </div>
        {action ? <div className="con-panel-action">{action}</div> : null}
      </div>
      {toolbar ? <div className="con-panel-toolbar">{toolbar}</div> : null}
      <div className="con-panel-body">{children}</div>
      {footer ? <div className="con-panel-footer">{footer}</div> : null}
    </motion.section>
  );
}

export type BentoSpan = "quarter" | "half" | "full";

export interface BentoProps {
  /** Accessible name of the group, e.g. "Ringkasan tahap Akuisisi". */
  label: string;
  children: ReactNode;
}

/**
 * The container for mixed summary content: figure cards, rings, charts, and
 * one short table in one grid, as cards of different widths side by side.
 * Two columns on a phone, six from 768, twelve from 1024. Every row must be
 * fully occupied, so a page asks `packBento` for the placements instead of
 * hoping the count tiles.
 */
export function Bento({ label, children }: BentoProps) {
  return (
    <div className="con-bento" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export interface BentoCellProps {
  /**
   * Where the cell sits: a placement from `packBento`, or one of the three
   * words (a quarter, a half, or the full width of the twelve columns).
   */
  span?: BentoSpan | BentoPlacement;
  children: ReactNode;
}

/**
 * One cell of a Bento. Its child (a Panel or a StatCard) carries the chrome
 * and takes the row height, so the cards of one row are one height. Give a
 * cell two short cards and they stack, sharing the height of a tall card in
 * the same row: that is how a figure or a ring sits beside a funnel without
 * an empty band under it.
 */
export function BentoCell({ span = "half", children }: BentoCellProps) {
  const placement = typeof span === "string" ? BENTO_WORDS[span] : span;
  const style = { "--bento-lg": placement.lg, "--bento-md": placement.md, "--bento-sm": placement.sm } as CSSProperties;
  return (
    <div className="con-bento-cell" data-span={typeof span === "string" ? span : String(placement.lg)} style={style}>
      {children}
    </div>
  );
}

export interface BentoFlowCard extends FlowCard {
  /** Stable key, also written as `data-card` for tests and deep links. */
  id: string;
  /** The card itself: a Panel or a StatCard. */
  node: ReactNode;
}

export interface BentoFlowProps {
  /** Accessible name of the group, e.g. "Tahap dan visual Financial". */
  label: string;
  cards: BentoFlowCard[];
}

/**
 * Cards of different widths and different heights with no empty band inside
 * any of them. Use this for a mix of charts, rings, lists, and figures whose
 * heights differ; use `Bento` only for a row of cards that share one anatomy
 * (four figure cards). The page says what each card wants (`width`, `height`,
 * whether it can `grow`) and `flowBento` cuts the list into bands of lanes.
 * One lane below 768, two from 768, the band's own lanes from 1024.
 */
export function BentoFlow({ label, cards }: BentoFlowProps) {
  const bands = flowBento(cards);
  return (
    <div className="con-flow" role="group" aria-label={label}>
      {bands.map((band, b) => (
        <div className="con-flow-band" data-lanes={band.lanes.length} key={cards[band.lanes[0]!.cards[0]!]!.id}>
          {band.lanes.map((lane, l) => (
            <div className="con-flow-lane" data-lane={l + 1} style={{ "--flow-span": lane.span } as CSSProperties} key={`${b}-${l}`}>
              {lane.cards.map((index) => {
                const card = cards[index]!;
                return (
                  <div className="con-flow-card" data-card={card.id} data-grow={lane.grow === index ? "" : undefined} key={card.id}>
                    {card.node}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
